<template>
  <v-progress-linear
    :active="waitForLoad"
    :model-value="loadNum"
    color="primary"
    absolute
    height="4"></v-progress-linear>
  <div style="display: flex; flex-direction: column; height: 100%">
    <div
      style="
        flex-shrink: 0;
        display: flex;
        gap: 8px;
        height: 78px;
        justify-content: stretch;
      ">
      <v-text-field
        v-model="search"
        label="Search"></v-text-field>
      <div style="display: flex; gap: 8px; align-items: center">
        <input
          type="datetime-local"
          v-model="dateTimeStart" />
        <input
          type="datetime-local"
          v-model="dateTimeEnd" />
        <v-btn
          icon
          rounded
          variant="tonal"
          size="small"
          @click="clearDateFilters"
          title="Clear date filters (show all logs - may be slow)">
          <v-icon>mdi-filter-off</v-icon>
        </v-btn>
        <v-btn
          icon
          rounded
          variant="tonal"
          size="small"
          @click="downloadLogs"
          title="Download filtered logs (max 10k rows)">
          <v-icon>mdi-download</v-icon>
        </v-btn>
      </div>
    </div>
    <div style="flex-grow: 1; overflow-y: auto">
      <v-infinite-scroll @load="loadMore">
        <v-virtual-scroll
          width="100%"
          :item-height="36"
          :renderless="true"
          :items="logs">
          <template v-slot:default="{ item }">
            <div
              class="pr-3"
              style="
                height: 30px;
                flex-shrink: 0;
                display: flex;
                gap: 6px;
                margin-bottom: 6px;
              ">
              <div
                class="infochip"
                :style="`background: ${getDisplayProperties(item.level).bg}40; color: ${getDisplayProperties(item.level).fg}87; width: 160px;`">
                {{ convertTimestamp(item.timestamp) }}
              </div>
              <div
                class="infochip"
                :style="`background: ${getDisplayProperties(item.level).bg}40; color: ${getDisplayProperties(item.level).fg}87; width: 160px;`">
                {{ item.context }}
              </div>
              <div
                class="infochip"
                :style="`background: ${getDisplayProperties(item.level).bg}40; color: ${getDisplayProperties(item.level).fg}87; flex-grow: 1;`">
                {{ item.message }}
              </div>
            </div>
          </template>
        </v-virtual-scroll>
      </v-infinite-scroll>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { $http } from '../../stores/http.js';
import { state } from '../../stores/deluge.js';
// replaced @vuepic/vue-datepicker with native inputs to reduce bundle size
import { user_search, loadNum, waitForLoad } from '../../utils/pseudoLoader.js';
const search = ref('');

watch(search, (newVal) => {
  user_search(searchQueryUpdated);
});
const dateTimeRange = ref(undefined);
// Default to last 24 hours to improve initial load performance
const now = new Date();
const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
// Format for datetime-local input (YYYY-MM-DDTHH:mm)
const formatForInput = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
const dateTimeStart = ref(formatForInput(yesterday));
const dateTimeEnd = ref(formatForInput(now));
const logs = ref([]);
const order = ref('desc');

function getDisplayProperties(lvl) {
  let bg = '#1a1a1f';
  let fg = '#ffffff';
  let icon = 'information';
  if (lvl === 1) {
    bg = '#f29a4e';
    fg = bg;
    icon = 'alert';
  } else if (lvl === 2) {
    bg = '#f25c5c';
    fg = bg;
    icon = 'alert-circle';
  }
  return { bg, fg, icon };
}

function convertTimestamp(timestamp) {
  const date = new Date(timestamp);
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
}
watch([dateTimeStart, dateTimeEnd], (newVal) => {
  user_search(searchQueryUpdated);
});
function getDatetimeRange() {
  let dateTimeStr = '';
  if (dateTimeStart.value && dateTimeEnd.value) {
    const max_datetime_iso = new Date(dateTimeEnd.value).toISOString();
    const min_datetime_iso = new Date(dateTimeStart.value).toISOString();
    dateTimeStr = `&max_datetime=${max_datetime_iso}&min_datetime=${min_datetime_iso}`;
  }
  return dateTimeStr;
}
function clearDateFilters() {
  dateTimeStart.value = undefined;
  dateTimeEnd.value = undefined;
}
async function searchQueryUpdated() {
  const new_logs = JSON.parse(
    await $http(
      'GET',
      `/get_logs?limit=100&offset=0&search_query=${
        search.value
      }${getDatetimeRange()}`
    )
  );
  logs.value = new_logs;
}
async function loadMore(resp) {
  const new_logs = JSON.parse(
    await $http(
      'GET',
      `/get_logs?limit=100&offset=${
        Object.keys(logs.value).length
      }&search_query=${search.value}${getDatetimeRange()}`
    )
  );
  logs.value = [...logs.value, ...new_logs];
  if (new_logs.length === 0) {
    resp.done('empty');
  } else {
    resp.done();
  }
}
onMounted(() => {
  console.log('Logger mounted');
  searchQueryUpdated();
});

async function downloadLogs() {
  // Build the download URL with current filters
  let url = `/download_filtered_logs?search_query=${encodeURIComponent(search.value)}`;
  if (dateTimeStart.value && dateTimeEnd.value) {
    const max_datetime_iso = new Date(dateTimeEnd.value).toISOString();
    const min_datetime_iso = new Date(dateTimeStart.value).toISOString();
    url += `&max_datetime=${encodeURIComponent(max_datetime_iso)}&min_datetime=${encodeURIComponent(min_datetime_iso)}`;
  }

  try {
    // Use $http to fetch with authentication, then trigger download
    const response = await $http('GET', url);

    // Create blob and download
    const blob = new Blob([response], { type: 'text/plain' });
    const blobUrl = URL.createObjectURL(blob);

    // Generate filename with timestamp
    const now = new Date();
    const filename = `canvaspro_logs_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}.txt`;

    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Failed to download logs:', error);
  }
}
</script>
